/**
 * Estructura estándar para un equipo de red procesado
 */
export interface NetworkDevice {
    ip: string;
    name: string;
    tipo: "PMPAP" | "PMPSM" | "RAJANT" | string;
    position: [number, number];
    status: string; // 'OK' | 'ALERT' | 'ALARM' | 'DOWN'
    padreIp?: string; // Para dibujar enlaces con el nodo padre
}

/**
 * Estructura de un Marcador Agrupado en el mapa (Cluster)
 */
export interface ClusteredMarker {
    id: string;
    position: [number, number];
    devices: NetworkDevice[];
    compositeImage: string;
}

/**
 * Distancia euclidiana entre coordenadas
 */
export function getGeoDistance(pos1: [number, number], pos2: [number, number]): number {
    if (!pos1 || !pos2) return 999;
    const dx = pos1[0] - pos2[0];
    const dy = pos1[1] - pos2[1];
    return Math.sqrt(dx * dx + dy * dy);
}

/**
 * Agrupa dispositivos visibles si la distancia entre ellos es menor al umbral (threshold).
 * Asigna la imagen según la combinación exacta de tipos presentes en el grupo.
 */
export function groupVisibleDevices(
    devices: NetworkDevice[],
    threshold: number,
    constantImages: Record<string, any>
): ClusteredMarker[] {
    const clusters: ClusteredMarker[] = [];
    const visited = new Set<string>();

    devices.forEach((device) => {
        if (visited.has(device.ip)) return;

        // Buscar dispositivos cercanos al punto actual
        const group: NetworkDevice[] = [device];
        visited.add(device.ip);

        devices.forEach((other) => {
            if (!visited.has(other.ip)) {
                const dist = getGeoDistance(device.position, other.position);
                if (dist <= threshold) {
                    group.push(other);
                    visited.add(other.ip);
                }
            }
        });

        // Detectar qué tipos de equipos componen este grupo
        const types = new Set(group.map((d) => d.tipo.toUpperCase()));
        const hasAP = types.has("PMPAP");
        const hasSM = types.has("PMPSM");
        const hasRAJ = types.has("RAJANT");

        // Selección de imagen compuesta según el trío, dúo o ícono individual
        let compositeImage = constantImages.IMAGES.onlysolar;

        if (hasAP && hasSM && hasRAJ) {
            compositeImage = constantImages.IMAGES.apsolar; // Combinado Triple
        } else if (hasAP && hasRAJ) {
            compositeImage = constantImages.IMAGES.apsolar; // AP + Rajant
        } else if (hasSM && hasRAJ) {
            compositeImage = constantImages.IMAGES.solarsm; // SM + Rajant
        } else if (hasAP && hasSM) {
            compositeImage = constantImages.IMAGES.onlyap; // AP + SM
        } else if (hasAP) {
            compositeImage = constantImages.IMAGES.onlyap;
        } else if (hasSM) {
            compositeImage = constantImages.IMAGES.onlypmp;
        } else if (hasRAJ) {
            compositeImage = constantImages.IMAGES.onlysolar;
        }

        // Coordenada media para centrar la marca en el grupo
        const avgLat = group.reduce((acc, d) => acc + d.position[0], 0) / group.length;
        const avgLng = group.reduce((acc, d) => acc + d.position[1], 0) / group.length;

        clusters.push({
            id: group.map((d) => d.ip).join("_"),
            position: [avgLat, avgLng],
            devices: group,
            compositeImage,
        });
    });

    return clusters;
}

/**
 * Genera la lista de polílineas (enlaces) uniendo únicamente equipos visibles
 */
/**
 * Genera la lista de polilíneas (enlaces) uniendo únicamente equipos que están visibles.
 * Soporta nomenclaturas ip_fuente/ip_cliente y sourceIp/targetIp.
 */
export function buildActiveLinks(
    visibleDevices: NetworkDevice[],
    rawPolylines: Array<any>
) {
    // Mapa de acceso rápido indexado por IP para los dispositivos actualmente visibles
    const deviceMap = new Map<string, NetworkDevice>();
    visibleDevices.forEach((dev) => deviceMap.set(dev.ip, dev));

    const activePolylines: Array<{ path: [number, number][]; colorLine: string }> = [];

    rawPolylines.forEach((link) => {
        // Tolerancia a múltiples nombres de propiedades provenientes de la API
        const sourceIp = link.ip_fuente || link.sourceIp || link.ip_padre;
        const targetIp = link.ip_cliente || link.targetIp || link.ip_hijo;

        const source = deviceMap.get(sourceIp);
        const target = deviceMap.get(targetIp);

        // Dibujar la línea solo si AMBOS extremos del enlace están activos/visibles en el mapa
        if (source && target) {
            // Validar que ambos tengan coordenadas válidas distintas de [0,0]
            const isValidSource = source.position[0] !== 0 && source.position[1] !== 0;
            const isValidTarget = target.position[0] !== 0 && target.position[1] !== 0;

            if (isValidSource && isValidTarget) {
                // Determinar color de línea según el estado del equipo cliente
                const color = link.colorLine || (target.status.toUpperCase() === 'OK' ? '#00FF00' : '#FF0000');

                activePolylines.push({
                    path: [source.position, target.position],
                    colorLine: color,
                });
            }
        }
    });

    return activePolylines;
}